import { type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Avatar } from "./Avatar";

/* ────────────────────────────────────────────
   RoomListItem
   ──────────────────────────────────────────── */

type RoomListItemProps = {
  /** 1-3 character abbreviation displayed in the avatar fallback */
  initials: string;
  /** Avatar image URL — falls back to initials when omitted */
  avatarSrc?: string;
  /** Primary label */
  title: string;
  /** Secondary label (e.g. "by Organization") */
  subtitle?: string;
  /** Optional description line below the title block */
  description?: string;
  /** Show a verified badge next to the subtitle */
  verified?: boolean;
  /** Show a new-content badge on the avatar */
  newContent?: boolean;
  /** Slot rendered on the right side of the title row (typically a Button) */
  action?: ReactNode;
  /** Additional class names merged onto the root element */
  className?: string;
  /** When true, hides the bottom border (last item in a list) */
  isLast?: boolean;
  /** Click handler for the entire item */
  onClick?: () => void;
};

function RoomListItem({
  initials,
  avatarSrc,
  title,
  subtitle,
  description,
  verified = false,
  newContent = false,
  action,
  className,
  isLast = false,
  onClick,
}: RoomListItemProps): React.JSX.Element {
  const Wrapper = onClick == null ? "div" : "button";

  return (
    <Wrapper
      type={onClick == null ? undefined : "button"}
      onClick={onClick}
      className={cn(
        "flex w-full gap-3 py-3 px-0 text-left",
        !isLast && "border-b border-ds-black/6",
        onClick != null && "cursor-pointer",
        className
      )}
    >
      {/* Left column: new-content dot + avatar */}
      <div className="flex shrink-0 items-start gap-1">
        {newContent ? (
          <span className="relative mt-3.75 flex size-2 shrink-0">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-ds-strawberry-400 opacity-75" />
            <span className="relative inline-flex size-full rounded-full bg-ds-strawberry-500" />
          </span>
        ) : (
          <span className="w-2 shrink-0" />
        )}
        {avatarSrc == null ? (
          <Avatar
            variant="initials"
            initials={initials}
            size="md"
            badge={null}
          />
        ) : (
          <Avatar
            variant="image"
            src={avatarSrc}
            alt={title}
            imageFit="scale-down"
            size="md"
            badge={null}
          />
        )}
      </div>

      {/* Center column: text content */}
      <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-clip">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col overflow-clip">
            <span className="truncate text-[13px]/5 font-semibold  tracking-[-0.13px] text-ds-black">
              {title}
            </span>
            {subtitle != null && (
              <span className="flex items-center gap-1">
                <span className="truncate text-[11px]/4  tracking-[-0.11px] text-ds-slate-700">
                  {subtitle}
                </span>
                {verified && <VerifiedBadge />}
              </span>
            )}
          </div>
          {action != null && <div className="shrink-0">{action}</div>}
        </div>
        {description != null && (
          <p className="text-[11px]/4  tracking-[-0.11px] text-ds-slate-400">
            {description}
          </p>
        )}
      </div>
    </Wrapper>
  );
}

/* ────────────────────────────────────────────
   Verified badge — inline SVG (Tabler rosette-discount-check)
   ──────────────────────────────────────────── */

function VerifiedBadge(): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="size-4 shrink-0 text-ds-primary"
    >
      <path d="M12.01 2.011a3.2 3.2 0 0 1 2.113 .797l.154 .145l.698 .698a1.2 1.2 0 0 0 .71 .341l.135 .008h1a3.2 3.2 0 0 1 3.195 3.018l.005 .182v1c0 .27 .092 .533 .258 .743l.09 .1l.697 .698a3.2 3.2 0 0 1 .147 4.382l-.145 .154l-.698 .698a1.2 1.2 0 0 0 -.341 .71l-.008 .135v1a3.2 3.2 0 0 1 -3.018 3.195l-.182 .005h-1a1.2 1.2 0 0 0 -.743 .258l-.1 .09l-.698 .697a3.2 3.2 0 0 1 -4.382 .147l-.154 -.145l-.698 -.698a1.2 1.2 0 0 0 -.71 -.341l-.135 -.008h-1a3.2 3.2 0 0 1 -3.195 -3.018l-.005 -.182v-1a1.2 1.2 0 0 0 -.258 -.743l-.09 -.1l-.697 -.698a3.2 3.2 0 0 1 -.147 -4.382l.145 -.154l.698 -.698a1.2 1.2 0 0 0 .341 -.71l.008 -.135v-1l.005 -.182a3.2 3.2 0 0 1 3.013 -3.013l.182 -.005h1a1.2 1.2 0 0 0 .743 -.258l.1 -.09l.698 -.697a3.2 3.2 0 0 1 2.27 -.944zm3.293 7.293a1 1 0 0 0 -1.414 0l-3.054 3.054l-.92 -.92a1 1 0 0 0 -1.414 0l-.127 .14a1 1 0 0 0 .127 1.274l1.627 1.627a1 1 0 0 0 1.414 0l3.761 -3.761a1 1 0 0 0 0 -1.414z" />
    </svg>
  );
}

export { RoomListItem, type RoomListItemProps };
